import React, { useCallback, useEffect, useRef } from 'react';
import { Animated, Pressable, StyleSheet, Text, View, } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import { clearPortionClarityToast, portionClarityToastMessageSelector, } from '../redux/appSlice';
import { theme } from '../theme';
const S = theme.colors;
/**
 * Post-save portion hint as a transient toast (domain term).
 * Implemented without react-native-paper Snackbar: Paper's Snackbar relies on
 * `use-latest-callback`, which can resolve to an incompatible/undefined export
 * under Metro when multiple versions exist (e.g. navigation vs paper).
 * Mounted at app shell so it survives navigation.goBack() from MealEntryScreen.
 */
export const PortionClarityToastHost = () => {
    const dispatch = useDispatch();
    const message = useSelector(portionClarityToastMessageSelector);
    const visible = Boolean(message);
    const insets = useSafeAreaInsets();
    const opacity = useRef(new Animated.Value(0)).current;
    const hideTimer = useRef(undefined);
    const dismiss = useCallback(() => {
        dispatch(clearPortionClarityToast());
    }, [dispatch]);
    useEffect(() => {
        if (hideTimer.current) {
            clearTimeout(hideTimer.current);
            hideTimer.current = undefined;
        }
        if (!visible) {
            Animated.timing(opacity, {
                toValue: 0,
                duration: 180,
                useNativeDriver: true,
            }).start();
            return;
        }
        Animated.timing(opacity, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
        }).start(() => {
            hideTimer.current = setTimeout(() => {
                dismiss();
            }, 4000);
        });
        return () => {
            if (hideTimer.current) {
                clearTimeout(hideTimer.current);
                hideTimer.current = undefined;
            }
        };
    }, [visible, message, opacity, dismiss]);
    return (<View style={styles.fill} pointerEvents="box-none">
      <Animated.View pointerEvents={visible ? 'auto' : 'none'} style={[
            styles.toast,
            {
                opacity,
                bottom: 48 + insets.bottom,
                left: 16 + insets.left,
                right: 16 + insets.right,
            },
        ]}>
        <Pressable accessibilityRole="alert" onPress={dismiss} style={styles.press}>
          <Text style={styles.text}>{message ?? ''}</Text>
        </Pressable>
      </Animated.View>
    </View>);
};
const styles = StyleSheet.create({
    fill: {
        ...StyleSheet.absoluteFill,
    },
    toast: {
        position: 'absolute',
        backgroundColor: S.portionToastBackground,
        borderWidth: StyleSheet.hairlineWidth,
        borderColor: S.portionToastBorder,
        borderRadius: 8,
        paddingVertical: 12,
        paddingHorizontal: 14,
        shadowColor: S.shadow,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.12,
        shadowRadius: 3,
        elevation: 2,
    },
    press: {
        flexShrink: 1,
    },
    text: {
        fontSize: 15,
        lineHeight: 21,
        color: S.portionToastText,
    },
});
